import { V2 } from "@ui/theme/DesignV2";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button, { TButtonVariant } from "@ui/button/Button";
import { Checkbox } from "@ui/checkbox/Checkbox";
import { Error } from "@ui/error/Error";
import { FileSelect } from "@ui/fileSelect/FileSelect";
import { IconClose } from "@ui/icons/IconClose";
import { IconCopy } from "@ui/icons/IconCopy";
import Input from "@ui/input/Input";
import { Link } from "@ui/link/Link";
import { Radio } from "@ui/radio/Radio";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { Spinner } from "@ui/spinner/Spinner";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import { CountriesData } from "@ui/utils/CountriesData";
import { isMobileViewport } from "@ui/utils/mobileViewport";
import { useWindowSize } from "@ui/utils/useResize";
import { useInsideUniversalDialog } from "src/view/components/universalDialog/dialogContext";

import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Slide, toast } from "react-toastify";
import { randomSHA256 } from "../tools/tools";
import { HLine } from "./HLine";
import { S_CONFIRM } from "./popup/general/ConfirmDialog";
import { S_SELECT_COUNTRIES_POPUP } from "./popup/SelectCountriesPopup";
import { S_SELECT_ITEMS_POPUP } from "./popup/SelectItemsPopup";
import styled from "styled-components";

// One visual system for every server-driven form: quiet labels, hints under the
// control, 44px fields with a single focus ring, sections split by hairlines.
const FormSurface = styled.div`
	display: contents;
	--f-ink: #0f172a; --f-label: #1e293b; --f-hint: #64748b; --f-line: #e2e8f0;
	--f-border: #cbd5e1; --f-border-hover: #94a3b8; --f-accent: ${V2.accent};

	[data-form-section] > [data-form-section-heading] { padding: 0; border: 0; margin: 0; }
	[data-form-section] > [data-form-section-heading] [data-typo] {
		display: flex !important; align-items: center; gap: 10px;
		font-size: 15px !important; font-weight: 600 !important; color: var(--f-ink) !important; letter-spacing: -0.01em;
	}
	&[data-form-context="dialog"] [data-form-section] {
		padding: 18px !important; border: 1px solid #e9ecf5 !important; border-radius: 16px !important;
		background: #fbfcff !important; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
	}
	/* Multi-section forms read as numbered steps. */
	:has(> [data-form-section] + [data-form-section]) { counter-reset: form-step; }
	[data-form-section] { counter-increment: form-step; }
	:has(> [data-form-section] + [data-form-section]) > [data-form-section] > [data-form-section-heading] [data-typo]::before {
		content: counter(form-step);
		display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
		width: 24px; height: 24px; border-radius: 8px; font-size: 12px; font-weight: 700; color: #fff;
		background: linear-gradient(135deg, #2b5c8a, #3f6e9a); box-shadow: 0 2px 6px rgba(30, 58, 95, 0.35);
	}

	[data-form-field] { min-width: 0; }
	[data-form-field] label,
	[data-form-field] div:has(> [role='combobox']) > div:first-child {
		font-size: 13px !important; font-weight: 600 !important; color: var(--f-label) !important;
		line-height: 1.4; opacity: 1 !important; letter-spacing: 0 !important;
	}
	[data-form-field] [data-typo]:has(> label) { opacity: 1 !important; }
	[data-form-field] [data-typo='caption']:not(:has(label)) {
		font-size: 12px !important; color: var(--f-hint) !important; line-height: 1.45 !important;
	}

	[data-input-control] { min-height: ${V2.controlHeight} !important; }
	[data-input-control]:not(:has(textarea)) { height: ${V2.controlHeight} !important; }
	[data-input-control]:not([data-input-validation='unvalid']):not([data-input-validation='valid']):not(:has(input:disabled, textarea:disabled)):not([aria-disabled='true']) {
		background: #fff !important; border: 1px solid #d9dfea !important;
		border-radius: 12px !important; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05) !important;
		transition: border-color 120ms ease, box-shadow 120ms ease;
	}
	[data-input-control]:hover:not(:focus-within):not([data-input-validation='unvalid']) { border-color: var(--f-border-hover) !important; }
	[data-input-control]:focus-within:not(:has(input:disabled)):not([data-input-validation='unvalid']),
	[data-input-control][role='combobox']:focus-visible {
		border-color: var(--f-accent) !important; box-shadow: 0 0 0 4px rgba(30, 58, 95, 0.14), 0 1px 2px rgba(16, 24, 40, 0.05) !important; outline: none;
	}
	[data-input-control]:has(input:disabled, textarea:disabled),
	[data-input-control][aria-disabled='true'] {
		background: #f8fafc !important; border-color: var(--f-line) !important; color: #94a3b8 !important;
	}
	[data-input-control] input:not([type='checkbox']):not([type='range']),
	[data-input-control][role='combobox'] { font-size: 15px; color: var(--f-ink); }
	[data-input-control] input::placeholder { color: #94a3b8; }
	[data-input-control][role='combobox'] svg path { fill: var(--f-hint); }
	@media (max-width: 768px) {
		[data-input-control] input:not([type='checkbox']):not([type='range']) { font-size: 16px; }
	}
`;

export interface TFormButton {
	title: string;
	onClick?: (data?: any) => void;
	sx?: RecursiveCSS;
	variant?: TButtonVariant;
	disabled?: boolean;
}

type TFormParserVariant = "default" | "reference";

const getReferenceFormButtonSx = (variant?: TFormParserVariant): RecursiveCSS => {
	if (variant !== "reference") return {};

	return {
		height: V2.controlHeight,
		padding: "0 14px",
		borderRadius: V2.controlRadius,
		boxShadow: "none",
		fontSize: "14px",
		fontWeight: 600,
		letterSpacing: "0",
		textTransform: "none",
	};
};

const getFormButtonSx = (variant?: TFormParserVariant): RecursiveCSS => {
	if (variant === "reference") return getReferenceFormButtonSx(variant);

	return {
		minHeight: V2.controlHeight,
		minWidth: "118px",
		padding: "0 14px",
		borderRadius: V2.controlRadius,
		fontWeight: 600,
		boxShadow: "none",
	};
};

export const FORM_ACTIONS_SX: RecursiveCSS = {
	position: "static",
	marginTop: V2.spacing,
	paddingTop: "12px",
	paddingBottom: 0,
	borderTop: "1px solid #E8EEF6",
	backgroundColor: "transparent",
	boxShadow: "none",
	backdropFilter: "none",
	alignItems: "center",
	justifyContent: "flex-end",
	flexWrap: "wrap",
};

const getValidationErrorFields = (validationError: any): string[] => {
	const details = Array.isArray(validationError?.details)
		? validationError.details
		: validationError?.details
			? [validationError.details]
			: [];
	const fields = new Set<string>();

	for (const detail of details) {
		const text = String(detail);
		const quotedValues = [...text.matchAll(/["']([^"']+)["']/g)].map(match => match[1]);
		for (const quotedValue of quotedValues) {
			const pathParts = quotedValue
				.replaceAll("[", ".")
				.replaceAll("]", "")
				.split(".")
				.map(part => part.trim())
				.filter(part => part && part.toLowerCase() !== "data");
			const field = pathParts.at(-1)
				?.replace(/([a-z0-9])([A-Z])/g, "$1_$2")
				.replace(/[^a-zA-Z0-9]+/g, "_")
				.replace(/^_+|_+$/g, "")
				.toLowerCase();
			if (field) fields.add(field);
		}
	}

	return [...fields];
};

interface IFormParser<T = unknown> {
	onSubmit: (data: T) => Promise<any>;
	onComplete?: () => void;
	onRequest: () => Promise<T>;
	onChange?: (data: T) => void;
	requestFormUpdater?: (updater: () => void) => void;
	ignoreObserver?: boolean;
	id?: string;
	secondaryButton?: TFormButton | null;
	primaryButton?: TFormButton | null;
	onComponentClick?: (item: IBlockItem) => void;
	sx?: RecursiveCSS;
	fullSizeButtons?: boolean;
	fieldsSx?: RecursiveCSS;
	actionsSx?: RecursiveCSS;
	scrollable?: boolean;
	buttons?: TFormButton[];
	grow?: boolean;
	size?: "small" | "medium" | "large";
	variant?: TFormParserVariant;
	previewMode?: boolean;
	highlightedFields?: string[];
	beforeActions?: React.ReactNode;
}

export const FormParser = <T,>({
	sx,
	grow,
	onComponentClick,
	ignoreObserver,
	secondaryButton,
	primaryButton,
	fullSizeButtons,
	fieldsSx,
	actionsSx,
	beforeActions,
	scrollable,
	onSubmit,
	onRequest,
	onComplete,
	onChange,
	buttons,
	size,
	variant = "default",
	requestFormUpdater,
	previewMode,
	highlightedFields,
}: IFormParser<T>) => {
	const { width, height } = useWindowSize();
	const isMobile = isMobileViewport(width, height);
	const insideDialog = useInsideUniversalDialog();
	const stretchActionButtons = fullSizeButtons ?? (isMobile && variant === "default");
	// Standalone mobile forms keep a safe-area bottom padding so the action row
	// clears the home indicator. Inside a dialog the sheet already provides that
	// padding, and applying both left dialog forms sitting 16px lower than the
	// hand-built action rows they sit next to.
	const mobileActionsSx: RecursiveCSS = isMobile && variant === "default" && !insideDialog
		? {
				marginTop: "12px",
				paddingTop: "12px",
				paddingBottom: "max(16px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
			}
		: {};
	const error = useState<any | null>(null);
	const data = useState<any | null>(null);
	const busy = useState<boolean>(false);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const submitInFlightRef = useRef(false);
	const errorAnchorRef = useRef<HTMLDivElement | null>(null);
	const [variableWatcher, _] = useState<string[]>([]);
	const [_r, setRerender] = useState<number>(0);
	if (!size) size = "medium";

	useEffect(() => {
		sendRequest();
		if (requestFormUpdater) {
			requestFormUpdater(() => {
				setRerender(Date.now());
			});
		}
	}, []);

	const currentError = error[0];
	const activeHighlightedFields = [
		...new Set([...(highlightedFields ?? []), ...getValidationErrorFields(currentError)]),
	];
	const highlightedFieldsKey = activeHighlightedFields.join("|");
	useEffect(() => {
		if (!data[0] || !activeHighlightedFields.length) return;
		const frame = requestAnimationFrame(() => {
			document
				.getElementById(`form_field_${activeHighlightedFields[0]}`)
				?.scrollIntoView({ behavior: "smooth", block: "center" });
		});
		return () => cancelAnimationFrame(frame);
	}, [data[0], highlightedFieldsKey]);

	useEffect(() => {
		if (!currentError) return;

		const frame = requestAnimationFrame(() => {
			errorAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			errorAnchorRef.current?.focus({ preventScroll: true });
		});

		return () => cancelAnimationFrame(frame);
	}, [currentError]);

	const sendRequest = async () => {
		if (unsavedChanges) {
			S_CONFIRM.invoke({
				message: "Confirm?",
				onResult: (val: "confirm" | "reject") => {
					if (val === "confirm") {
						reloadData();
					}
				},
			});
		} else {
			reloadData();
		}
	};

	const reloadData = async () => {
		setUnsavedChanges(false);
		busy[1](true);
		error[1](null);
		try {
			const result = await onRequest();
			setData(result);
		} catch (cause) {
			error[1]({ code: -1, message: cause instanceof globalThis.Error ? cause.message : "Failed to load form" });
		} finally {
			busy[1](false);
		}
	};

	const setData = (result: any) => {
		if (result && typeof result === "object") {
			let err = false;

			if ("error" in result && typeof result.error === "object" && "code" in result.error) {
				err = true;
				error[1](result.error);
			}

			if (!err && "code" in result && "message" in result) {
				err = true;
				error[1](result);
			}

			if (!err && result) {
				setUnsavedChanges(false);
				let isApiResponse = false;
				if (result.data && result.data.data) isApiResponse = true;
				if (isApiResponse) {
					data[1](result.data);
				} else {
					if ("data" in result) data[1](result.data);
					else data[1](result);
				}
			}
		} else {
			error[1]({ code: -1, message: "wrong data" });
		}
	};

	const onFieldChange = (fieldName?: string) => {
		setUnsavedChanges(true);
		if (fieldName) {
			for (const i of variableWatcher) {
				if (fieldName === i) {
					setRerender(Date.now());
					break;
				}
			}
		}
		if (onChange) onChange(data[0]);
	};

	const sendSubmit = async (_e?: any) => {
		if (previewMode || submitInFlightRef.current) return;
		submitInFlightRef.current = true;
		busy[1](true);
		error[1](null);
		try {
			const result = await onSubmit(data[0]);

			setData(result);
			onComplete && !result?.error && onComplete();
			if (result?.error) error[1](result.error);
		} catch (cause) {
			error[1]({ code: -1, message: cause instanceof globalThis.Error ? cause.message : "Failed to submit form" });
		} finally {
			submitInFlightRef.current = false;
			busy[1](false);
		}
	};

	let content = null;

	const renderButtons = [...(buttons ?? [])];

	if (secondaryButton && !previewMode) {
		renderButtons.push({
			title: secondaryButton.title ?? "Restore",
			onClick:
				secondaryButton?.onClick ??
				(() => {
					sendRequest();
				}),
			variant: secondaryButton?.variant ?? ("secondarySmall" as TButtonVariant),
			sx: secondaryButton?.sx,
			disabled: secondaryButton?.disabled,
		});
	}

	if (primaryButton !== null && !previewMode) {
		let primaryTitle = primaryButton?.title ?? "Save";
		const variantButton: TButtonVariant = primaryButton?.variant ?? ("primarySmall" as TButtonVariant);

		if (!ignoreObserver) {
			if (unsavedChanges) {
				primaryTitle = primaryButton?.title ?? "Save (unsaved changes)";
			}
		}

		renderButtons.push({
			title: primaryTitle,
			onClick:
				primaryButton?.onClick ??
				((e?: any) => {
					if (!onSubmit) return;
					sendSubmit(e);
				}),
			variant: variantButton,
			sx: primaryButton?.sx,
			disabled: primaryButton?.disabled,
		});
	}

	const buttonsElement = renderButtons.map((button, index) => (
		<Button
			key={index}
			sx={{ ...getFormButtonSx(variant), ...(stretchActionButtons ? { flex: "1 1 0", minWidth: 0 } : {}), ...button.sx }}
			variant={button.variant ?? ("primarySmall" as TButtonVariant)}
			disabled={button.disabled || busy[0]}
			onClick={(e?: any) => {
				if (e && e.shiftKey) {
					e.preventDefault();
					e.stopPropagation();
					return;
				}

				if (button.onClick) {
					button.onClick(data[0]);
				}
			}}
			title={button.title ?? "untitled button"}
		/>
	));

	const registerLinkedVariable = (name: string) => {
		if (variableWatcher.indexOf(name) !== -1) return;
		variableWatcher.push(name);
	};

	const initialLoading = busy[0] && !data[0];

	if (data[0]) {
		content = (
			<>
				<Block
					previewMode={previewMode}
					variant={variant}
					size={size}
					parentIndex="_root_"
					registerLinkedVariable={registerLinkedVariable}
					onComponentClick={onComponentClick}
					data={data[0].data ?? data[0]}
					parent={null}
					parentOptions={fieldsSx ? { sx: fieldsSx } : undefined}
					highlightedFields={activeHighlightedFields}
					onFieldChange={onFieldChange}
				/>
			</>
		);
	} else if (initialLoading) {
		content = (
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 0" }}>
				<Spinner />
			</Box>
		);
	}

	if (scrollable) {
		if (!sx) sx = {};
		sx = {
			...sx,
			height: "100%",
			overflowY: "auto",
			flexGrow: 1,
		};
	}

	const errorBanner = currentError ? (
		<div ref={errorAnchorRef} role="alert" aria-live="assertive" tabIndex={-1}>
			<Error error={currentError} />
		</div>
	) : null;

	return (
		<FormSurface data-form-parser data-form-context={insideDialog ? "dialog" : "page"}>
			{scrollable && errorBanner && (
				<>
					{errorBanner}
					<Spacer size="20px" />
				</>
			)}

			<Box
				style={{
					maxWidth: grow ? "auto" : "800px",
					...sx,
				}}
				sx={{
					width: "100%",
					position: "relative",
					display: "flex",
					flexDirection: "column",
					minWidth: 0,
				}}>
				{!scrollable && (
					<>
						{errorBanner && (
							<>
								{errorBanner}
								<Spacer size="20px" />
							</>
						)}
					</>
				)}
				{scrollable && (
					<Box
						sx={{
							position: "absolute",
							width: "100%",
							padding: "0px 3px 3px 0px",
						}}>
						{content}
						{!initialLoading && beforeActions}
					</Box>
				)}
				{!scrollable && (
					<>
						{content}
						{!initialLoading && beforeActions}
						{!previewMode && !initialLoading && buttonsElement.length > 0 && (
						<Box sx={{ ...FORM_ACTIONS_SX, ...mobileActionsSx, ...actionsSx }}>
								<HBox gap="12px" sx={{ alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", width: "100%" }}>
									{!stretchActionButtons && <Spacer grow />}
									{buttonsElement}
								</HBox>
							</Box>
						)}
					</>
				)}
			</Box>
			{scrollable && !previewMode && !initialLoading && buttonsElement.length > 0 && (
				<Box sx={{ ...FORM_ACTIONS_SX, ...mobileActionsSx, ...actionsSx }}>
					<HBox gap="12px" sx={{ alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", width: "100%" }}>
						{!stretchActionButtons && <Spacer grow />}
						{buttonsElement}
					</HBox>
				</Box>
			)}
		</FormSurface>
	);
};

const parseBlickItems = (data: any, isParentLinked: boolean | undefined, parent: any, registerLinkedVariable: (name: string) => any): IBlockItem[] => {
	const items: IBlockItem[] = [];

	if (data && typeof data === "object") {
		for (const i in data) {
			if (i.startsWith("$__")) continue;

			let options: Record<string, any> | null = null;

			if (isParentLinked) {
				const pname = data[i];
				for (const j in parent) {
					if (j.startsWith("$__" + pname + "_")) {
						const oname = j.replace("$__" + pname + "_", "");
						if (!options) options = {};
						if (oname) options[oname] = parent[j];
					}
					if (j === "$__" + pname) {
						if (!options) options = {};
						options = { ...options, ...parent[j] };
					}
				}
			} else {
				for (const j in data) {
					if (j.startsWith("$__" + i + "_")) {
						const oname = j.replace("$__" + i + "_", "");
						if (!options) options = {};
						if (oname) options[oname] = data[j];
					}
					if (j === "$__" + i) {
						if (!options) options = {};
						options = { ...options, ...data[j] };
					}
				}
			}

			if (options?.decorator) {
				const decorator = options.decorator as Record<string, any>;
				if (decorator.gridTemplateColumns) options.gridColumns = decorator.gridTemplateColumns;
				if (decorator.sx) options.sx = decorator.sx;
				if (decorator.blockless) options.blockless = decorator.blockless;
			}

			items.push({
				name: i,
				content: data[i],
				options,
				container: isParentLinked ? parent : data,
				registerLinkedVariable,
			});
		}
	} else if (data && typeof data !== "object") {
		if (Array.isArray(parent)) {
			const index = parent.findIndex((val: any) => val === data);
			if (index > -1 && data) {
				items.push({
					name: index + "",
					content: data,
					options: { label: null, type: "plain" },
					container: parent,
					registerLinkedVariable,
				});
			}
		} else {
			console.warn("Wrong block content", data);
		}
	}

	return items;
};

interface IBlockProps {
	data: any;
	parent: any;
	parentIndex?: string;
	parentOptions?: Record<string, any> | null;
	isParentLinked?: boolean;
	previewMode?: boolean;
	onComponentClick?: (component: IBlockItem) => void;
	registerLinkedVariable: (name: string) => any;
	size: "small" | "medium" | "large";
	variant?: TFormParserVariant;
	possibleValues?: any[];
	highlightedFields?: string[];
	onFieldChange?: (fieldName?: string) => void;
}

interface IBlockItem {
	name: string;
	content: any;
	options: Record<string, any> | null;
	container: any;
	linked?: boolean;
	registerLinkedVariable: (name: string) => any;
}

export const Block = (props: IBlockProps) => {
	const [state, setState] = useState<number>(0);

	let items: IBlockItem[] = parseBlickItems(props.data, props.isParentLinked, props.parent, props.registerLinkedVariable);

	let decorator = null;

	if (props.parent === null && props.data && typeof props.data === "object" && "$__decorator" in props.data) {
		decorator = props.data["$__decorator"] as Record<string, any>;
	}

	if (props.parentOptions && props.parentOptions.decorator) {
		decorator = props.parentOptions.decorator as Record<string, any>;
	}

	if (decorator) {
		if (decorator.order && Array.isArray(decorator.order)) {
			items = items.filter(val => decorator.order.indexOf(val.name) !== -1);
			items.sort((a, b) => {
				const i1 = decorator.order.indexOf(a.name);
				const i2 = decorator.order.indexOf(b.name);
				if (i1 === -1 && i2 === -1) return 0;
				if (i1 === -1) return 1;
				if (i2 === -1) return -1;
				return i1 - i2;
			});
		} else if (decorator.blocks) {
			const blockItems: IBlockItem[] = [];

			for (const block of decorator.blocks) {
				let workingItems = [...items];

				if (block.order && Array.isArray(block.order)) {
					workingItems = workingItems.filter(val => block.order.indexOf(val.name) !== -1);
				}

				if (block.order && Array.isArray(block.order)) {
					workingItems.sort((a, b) => {
						const i1 = block.order.indexOf(a.name);
						const i2 = block.order.indexOf(b.name);
						if (i1 === -1 && i2 === -1) return 0;
						if (i1 === -1) return 1;
						if (i2 === -1) return -1;
						return i1 - i2;
					});
				}

				if (workingItems.length === 0 || workingItems.every(item => item.options?.hidden)) continue;

				const opt: Record<string, any> = {};
				if (block.gridTemplateColumns) opt.gridColumns = block.gridTemplateColumns;
				if (block.sx) opt.sx = block.sx;
				if (block.blockless) opt.blockless = block.blockless;

				const content: Record<string, string> = {};
				for (const i in workingItems) {
					const itm = workingItems[i];
					content["$link_" + i] = itm.name;
				}

				if (block.blockless !== undefined && block.blockless !== null) opt.blockless = block.blockless;
				if (props.parentOptions && props.parentOptions.onDeleteClick) opt.onDeleteClick = props.parentOptions.onDeleteClick;

				blockItems.push({
					registerLinkedVariable: props.registerLinkedVariable,
					name: block.title,
					content,
					options: opt,
					container: props.data,
					linked: true,
				});
			}

			items = blockItems;
		}
	}

	const returnContent = items.map((val: IBlockItem, index: number) => {
		const key = (props.parentIndex ?? "^") + "_" + index;
		const sx: RecursiveCSS = {};

		if (val.options && val.options.hidden) {
			return null;
		}

		if (props.parentOptions && props.parentOptions.onDeleteClick) {
			if (val.options && val.options.type === "delete_button") {
				val.options.onDeleteClick = props.parentOptions.onDeleteClick;
			}
		}

		if (val.options && val.options.linked) {
			let value = null;
			const linkedKey = val.options.linked;

			if (typeof linkedKey === "string" && linkedKey.includes(".") && val.container) {
				const parts = linkedKey.split(".");
				let current: any = val.container;
				for (let i = 0; i < parts.length && current !== undefined && current !== null; i += 1) {
					current = current[parts[i]];
				}
				value = current;
			} else if (linkedKey !== undefined && linkedKey !== null && val.container) {
				if (typeof linkedKey === "string" || typeof linkedKey === "number") {
					value = (val.container as Record<string, unknown>)[String(linkedKey)];
				}
			}

			props.registerLinkedVariable(val.options.linked);
			if (Array.isArray(val.options.linked_value)) {
				if (!val.options.linked_value || !val.options.linked_value.includes(value)) {
					return null;
				}
			} else if (value !== val.options.linked_value) {
				return null;
			}
		}

		let innerObject = false;
		if (val.name && val.name.startsWith("$link_") && typeof val.container[val.content] === "object") {
			innerObject = true;
			val.name = val.content;
			val.content = val.container[val.content];
		}

		if (typeof val.content === "object") {
			const valDecorator = val.options?.decorator as Record<string, any> | undefined;
			const hasDecoratedBlocks = !!(valDecorator && valDecorator.blocks && Array.isArray(valDecorator.blocks));

			let contentBlock = null;
			if (!Array.isArray(val.content)) {
				let posval = undefined;
				if (val.options && val.options.possible_values && Array.isArray(val.options.possible_values) && val.options.possible_values.length > 0) {
					posval = val.options.possible_values;
				}

				contentBlock = (
					<Block
						previewMode={props.previewMode}
						size={props.size}
						onComponentClick={props.onComponentClick}
						key={"b" + key}
						data={val.content}
						isParentLinked={val.linked}
						parent={props.data}
						parentOptions={val.options}
						parentIndex={index + "_"}
						registerLinkedVariable={props.registerLinkedVariable}
						highlightedFields={props.highlightedFields}
						onFieldChange={props.onFieldChange}
					/>
				);

				if (posval) {
					contentBlock = (
						<>
							{contentBlock}
							<HBox>
								<Spacer grow />
								<Button
									variant="secondarySmall"
									onClick={(): void => {
										S_SELECT_ITEMS_POPUP.invoke({
											title: "Select " + createName(val.name),
											items: posval,
											multi: false,
											gridColumns: "max-content auto max-content min-content",
											searchable: false,
											onSelected: itemsSelected => {
												if (itemsSelected && itemsSelected.length > 0) {
													const keys = Object.keys(val.content);
													for (const i of keys) {
														if (i in itemsSelected[0]) {
															val.content[i] = itemsSelected[0][i];
														}
													}
													if (props.data && val.name in props.data) {
														props.data[val.name] = { ...val.content };
													}
													setState(state + 1);
													props.onFieldChange?.();
												}
											},
											onClose: () => {},
										});
									}}
								>
									{createName("Choose " + val.name)}
								</Button>
							</HBox>
						</>
					);
				}
			} else {
				contentBlock = <ArrayBlock key={"arr" + key} val={val} parentKey={key} index={index} blockProps={props} />;
			}

			if (hasDecoratedBlocks || innerObject) {
				let withoutTitle = !(val.options?.decorator as any)?.title;
				if ((val.options?.decorator as any)?.title === "") withoutTitle = false;
				if (withoutTitle) return contentBlock;
			}

			if (val.options?.blockless && (val.options?.decorator as any)?.title) {
				return (
					<VBox gap="4px" key={key}>
						<VBox sx={{ paddingBottom: "8px" }}>
							<Typo variant="medium" bold>
								{(val.options?.decorator as any)?.title}
							</Typo>
							{(val.options?.decorator as any)?.subtitle && (
								<Typo variant={props.size === "small" ? "caption" : "medium"}>{(val.options?.decorator as any)?.subtitle}</Typo>
							)}
						</VBox>
						{contentBlock}
					</VBox>
				);
			}

			if (val.options?.blockless) return contentBlock;

			return (
				<Box
					data-form-section
					sx={{
						padding: V2.spacing,
						backgroundColor: "#FFFFFF",
						borderRadius: V2.surfaceRadius,
						boxShadow: "none",
						border: "1px solid #E5E8F0",
						display: "grid",
						gap: V2.spacing,
						pointerEvents: val.options && val.options.disabled ? "none" : "all",
						...sx,
					}}
					key={key}>
					{!Array.isArray(props.data) && val.name && (
						<Box data-form-section-heading sx={{ marginBottom: "2px", display: "flex", flexDirection: "column", gap: "6px" }}>
							<Typo variant="medium" bold>
								{(val.options?.decorator as any)?.title ?? createName(val.name)}
							</Typo>
							{(val.options?.decorator as any)?.subtitle && (
								<Typo variant={props.size === "small" ? "caption" : "medium"}>{(val.options?.decorator as any)?.subtitle}</Typo>
							)}
						</Box>
					)}
					{contentBlock}
				</Box>
			);
		}

		return (
			<InputBlock
				previewMode={props.previewMode}
				size={props.size}
				variant={props.variant}
				onComponentClick={props.onComponentClick}
				highlightedFields={props.highlightedFields}
				key={key}
				data={val}
				onFieldChange={props.onFieldChange}
			/>
		);
	});

	if (Array.isArray(props.parent)) return returnContent;

	let gridBox: RecursiveCSS | null = null;
	if (props.parentOptions && props.parentOptions.gridColumns) {
		gridBox = {
			display: "grid",
			gridTemplateColumns: props.parentOptions.gridColumns,
		};
	}
	if (props.parentOptions && props.parentOptions.sx && typeof props.parentOptions.sx === "object") {
		const parentSX = props.parentOptions.sx as RecursiveCSS;
		gridBox = {
			...gridBox,
			...parentSX,
		};
	}

	return (
		<Box
			sx={{
				display: "grid",
				gap: V2.spacing,
				...gridBox,
			}}>
			{returnContent}
		</Box>
	);
};

interface IArrayBlockProps {
	val: IBlockItem;
	parentKey: string;
	index: number;
	blockProps: IBlockProps;
}

const ARRAY_BLOCK_LIMIT = 10;

const ArrayBlock = ({ val, parentKey, index, blockProps }: IArrayBlockProps) => {
	const [state, setState] = useState<number>(0);
	const [showAll, setShowAll] = useState<boolean>(false);

	const isItemViewable = (item: any) => {
		if (val.options?.hideEmpty && typeof item === "object") {
			if (isObjectEmpty(item)) {
				return false;
			}
		}
		if (blockProps.previewMode && isObjectEmpty(item)) {
			return false;
		}
		return true;
	};

	const getViewableItemsCount = () => {
		if (!val.content || !Array.isArray(val.content)) return 0;
		let count = 0;
		for (const item of val.content) {
			if (isItemViewable(item)) {
				count++;
			}
		}
		return count;
	};

	const totalItems = getViewableItemsCount();
	const isLimited = totalItems > ARRAY_BLOCK_LIMIT && !showAll;

	let asTileSX: RecursiveCSS = {};
	let asTileSXItem: RecursiveCSS = {};
	if (val.options?.astiles) {
		asTileSX = {
			display: "flex",
			flexWrap: "wrap",
		};

		asTileSXItem = {
			border: "1px solid #E5EAF2",
			borderRadius: "10px",
			boxSizing: "border-box",
			padding: "12px",
			backgroundColor: "#FFFFFF",
		};
	}

	return (
		<Box
			sx={{
				display: "grid",
				gap: val.options?.asblocks ? "20px" : "12px",
				...asTileSX,
			}}>
			{val.content.map((arritem: any, arrindex: number) => {
				if (isLimited && arrindex >= ARRAY_BLOCK_LIMIT) return null;
				if (!isItemViewable(arritem)) return null;

				const innerKey = parentKey + "_" + arrindex;

				let gridSX: RecursiveCSS = {};
				if (val.options && val.options.gridColumns) {
					gridSX = {
						gridTemplateColumns: val.options.gridColumns,
						display: "grid",
						gap: "20px",
						alignItems: "center",
					};
				}
				if (val.options && val.options.sx && typeof val.options.sx === "object") {
					const valSX = val.options.sx as RecursiveCSS;
					gridSX = {
						...gridSX,
						...valSX,
					};
				}

				let rmbutton = !val.options?.ignore_delete_button && val.content && val.content.length > 1;
				if (blockProps.previewMode) {
					rmbutton = false;
				}

				let onRMClick = null;
				if (rmbutton || (val.content && val.content.length > 1 && val.options?.ignore_delete_button)) {
					onRMClick = () => {
						if (val.content && Array.isArray(val.content)) {
							val.content.splice(arrindex, 1);
							setState(state + 1);
							blockProps.onFieldChange?.();
						}
					};
				}

				let Container = (cProps: { children: React.ReactNode }) => <Fragment key={innerKey + "_A"}>{cProps.children}</Fragment>;
				if (val.options && val.options.asblocks) {
					Container = (cProps: { children: React.ReactNode }) => (
						<VBox
							key={innerKey + "_A"}
							sx={{
								borderRadius: "12px",
								borderColor: "#E5EAF2",
								borderStyle: "solid",
								borderWidth: "1px",
								backgroundColor: "#FFFFFF",
								boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
							}}>
							<HBox
								sx={{
									minHeight: "42px",
									alignItems: "center",
									backgroundColor: "#F8FAFC",
									padding: "10px 12px 10px 16px",
								}}>
								<Typo>{arrindex + 1 + ". " + (val.options?.itemtitle ?? createName(val.name))}</Typo>

								{rmbutton && (
									<Button
										variant="secondarySmall"
										sx={{
											...getReferenceFormButtonSx(blockProps.variant),
											marginLeft: "auto",
											padding: "4px",
										}}
										onClick={() => {
											if (onRMClick) onRMClick();
										}}>
										<IconClose />
									</Button>
								)}
							</HBox>
							<Box sx={{ padding: "18px 18px 20px 18px" }}>{cProps.children}</Box>
						</VBox>
					);
				}

				return (
					<Container key={innerKey + "_container"}>
						<Box
							key={innerKey + "_A"}
							sx={{
								display: "grid",
								alignItems: "center",
								gap: "12px",
								gridTemplateColumns: "1fr " + (val.options?.asblocks ? "" : rmbutton ? "max-content" : ""),
								borderBottom:
									val.options?.borders && arrindex < val.content.length - 1 ? "1px solid #E5EAF2" : "0px solid rgba(0,0,0,.2)",
								paddingBottom: val.options?.borders && arrindex < val.content.length - 1 ? "22px" : "0",
								marginBottom: val.options?.borders && arrindex < val.content.length - 1 ? "16px" : "0",
								...asTileSXItem,
							}}>
							<Box sx={{ ...gridSX }}>
									<Block
										previewMode={blockProps.previewMode}
										size={blockProps.size}
										variant={blockProps.variant}
										registerLinkedVariable={blockProps.registerLinkedVariable}
										highlightedFields={blockProps.highlightedFields}
										onComponentClick={blockProps.onComponentClick}
										key={"b" + parentKey}
										data={arritem}
										isParentLinked={val.linked}
										parent={val.options && val.options.decorator ? blockProps.data : val.content}
										parentOptions={{ ...val.options, onDeleteClick: onRMClick as any }}
										parentIndex={index + innerKey + "_A"}
									onFieldChange={blockProps.onFieldChange}
									/>
								</Box>

							{rmbutton && !val.options?.asblocks ? (
								<Button
									variant="secondarySmall"
									sx={{ padding: "4px" }}
									onClick={() => {
										if (onRMClick) onRMClick();
									}}>
									<IconClose />
								</Button>
							) : null}
						</Box>
						{arrindex + 1 < val.content.length && val.options?.aslist && <HLine key={innerKey + "_B"} color="rgba(0,0,0,.1)" />}
					</Container>
				);
			})}

			{totalItems > ARRAY_BLOCK_LIMIT && (
				<HBox>
					<Spacer grow />
					<Button variant="secondarySmall" sx={getReferenceFormButtonSx(blockProps.variant)} onClick={() => setShowAll(!showAll)}>
						{showAll ? "Show less" : `Show all (${totalItems})`}
					</Button>
				</HBox>
			)}

			{!val.options?.hide_add_button && !blockProps.previewMode && (
				<HBox>
					<Spacer grow />
					{val.options?.max && val.content.length >= parseInt(val.options.max) ? null : (
						<Button
							variant="secondarySmall"
							sx={getReferenceFormButtonSx(blockProps.variant)}
							onClick={() => {
								const values = val.options?.possible_values;

								if (val.options?.possible_values && Array.isArray(values)) {
									S_SELECT_ITEMS_POPUP.invoke({
										title: "Select " + createName(val.name),
										items: values,
										multi: val.options?.max ? parseInt(val.options.max) > 1 : true,
										gridColumns: "max-content auto max-content min-content",
										searchable: false,
										onSelected: itemsSelected => {
											if (itemsSelected) {
												for (const item of itemsSelected) {
													if (val.content && Array.isArray(val.content)) {
														if (item.string) {
															val.content.push(item.name);
															continue;
														}
														const original = val.content[0];
														if (!original) {
															console.warn("Original item not found for adding new item");
															return;
														}

														const newitem = JSON.parse(JSON.stringify(original));
														for (const i in original) {
															if (i in item) newitem[i] = item[i];
														}
														val.content.push(newitem);
													}
												}
												setState(state + 1);
												blockProps.onFieldChange?.();
											}
										},
										onClose: () => {},
									});
									return;
								}

								if (val.options?.type === "country") {
									S_SELECT_COUNTRIES_POPUP.invoke({
										onSuccess: countries => {
											for (let countryIndex = 0; countryIndex < countries.length; countryIndex++) {
												const country = countries[countryIndex];
												const newblock = JSON.parse(JSON.stringify(val.content[0]));
												for (const key in country) {
													if (key in newblock) newblock[key] = (country as any)[key];
												}
												let found = false;
												for (const j of val.content) {
													if (JSON.stringify(j).toLowerCase() === JSON.stringify(newblock).toLowerCase()) {
														found = true;
														break;
													}
												}
												if (!found) val.content.push(newblock);
											}
											setState(state + 1);
											blockProps.onFieldChange?.();
										},
									});
									return;
								}

								if (val.content && val.content.length) {
									const newItemData = JSON.parse(JSON.stringify(val.content[val.content.length - 1]));
									val.content.push(newItemData);
									setState(state + 1);
									blockProps.onFieldChange?.();
								}
							}}>
							{val.options?.addbuttontitle ?? "Add " + createName(val.name)}
						</Button>
					)}
				</HBox>
			)}
		</Box>
	);
};

const InputBlock = ({
	previewMode,
	size,
	variant,
	data,
	highlightedFields,
	onComponentClick,
	onFieldChange,
}: {
	previewMode?: boolean;
	size: "small" | "medium" | "large";
	variant?: TFormParserVariant;
	data: IBlockItem;
	highlightedFields?: string[];
	onComponentClick?: (component: IBlockItem) => void;
	onFieldChange?: (fieldName?: string) => void;
}) => {
	const [state, setState] = useState<number>(0);

	let name = data.name;
	let dataContent = data.content;
	if (name.startsWith("$link")) {
		name = data.content;
		dataContent = data.container[name];
	}

	let type = data.options?.type ?? "text";
	if (type !== "switchbox" && typeof dataContent === "boolean") type = "boolean";

	let switchboxLabels = undefined;
	if (type === "switchbox" && data.options?.switchbox_labels && typeof data.options.switchbox_labels === "object") {
		switchboxLabels = {
			yes: data.options.switchbox_labels.yes ?? "Yes",
			no: data.options.switchbox_labels.no ?? "No",
		};
	}

	let disabled = false;
	if (data.options?.disabled) disabled = true;
	if (previewMode) disabled = true;

	if (data.options?.hidden) return null;

	let units = "";
	if (data.options?.type === "%") units = " %";
	if (data.options?.type === "amount") {
		const currency = data.container?.currency;
		units = typeof currency === "string" && currency.trim() ? ` ${currency.trim()}` : "";
	}
	if (data.options?.units) units = data.options.units;

	let content = null;

	let label: string | undefined = createName(name);
	if (data.options?.label_uppercase) label = label.toUpperCase();
	if (data.options?.label) label = data.options.label;
	if (data.options?.label === null || data.options?.label === "") label = undefined;

	let sx = null;
	if (data.options?.sx && typeof data.options.sx === "object") sx = data.options.sx as RecursiveCSS;

	if (previewMode) {
		if (type === "select" || type === "country") type = "text";
	}

	switch (type) {
		case "select": {
			const values = data.options?.values ?? [];
			content = (
				<Select
					size={size}
					variant={variant}
					disabled={disabled}
					name={name}
					label={label}
					data={data.container}
					values={values as any[]}
					searchable={Boolean(data.options?.searchable)}
					searchPlaceholder={data.options?.search_placeholder}
					searchMode={data.options?.search_mode === "prefix" ? "prefix" : undefined}
					onChange={() => onFieldChange?.(name)}
				/>
			);
			break;
		}

		case "file":
			content = (
				<FileSelect
					previewMode={previewMode}
					size={size}
					disabled={disabled}
					name={name}
					title={label}
					subtitle={data.options?.subtitle}
					data={data.container}
					value={undefined}
				/>
			);
			break;

		case "link":
			content = (
				<Link
					size={size}
					disabled={disabled}
					name={name}
					title={label}
					subtitle={data.options?.subtitle}
					data={data.container}
					icon={data.options?.icon}
					value={undefined}
				/>
			);
			break;

		case "radio": {
			const valuesRadio = data.options?.values ?? [];
			content = (
				<Radio
					size={size}
					disabled={disabled}
					name={name}
					label={label}
					data={data.container}
					values={valuesRadio as any[]}
					value={data.options?.value}
					onChange={() => onFieldChange?.(name)}
				/>
			);
			break;
		}

		case "delete_button":
			if (data.options?.onDeleteClick === undefined) return null;
			content = (
				<Button
					variant="secondarySmall"
					sx={{
						...getReferenceFormButtonSx(variant),
						padding: "4px",
						maxWidth: "25px",
						minWidth: "25px",
						maxHeight: "25px",
						minHeight: "25px",
						...(data.options?.sx as any),
					}}
					onClick={() => {
						if (data.options?.onDeleteClick && typeof data.options.onDeleteClick === "function") {
							(data.options as any).onDeleteClick();
						}
					}}>
					<IconClose />
				</Button>
			);
			break;

		case "country":
			content = (
				<Select
					size={size}
					variant={variant}
					disabled={disabled}
					name="cc"
					label={label}
					onChange={val => {
						data.container[name] = val.alpha2;
						onFieldChange?.(name);
					}}
					data={{ cc: data.container[name] }}
					values={CountriesData.countries}
				/>
			);
			break;

		case "":
			break;

		case "plain":
			if (!data.container[name]) return null;
			content = (
				<Typo variant="medium" sx={sx ?? undefined}>
					{data.container[name]}
				</Typo>
			);
			break;

		case "button":
			if (!data.container[name]) return null;
			content = (
				<Button
					disabled={disabled}
					onClick={() => {
						if (onComponentClick) onComponentClick(data);
					}}>
					{data.container[name]}
				</Button>
			);
			break;

		case "spacer":
			if (!data.container[name]) return null;
			content = <Spacer size={data.container[name]} />;
			break;

		case "date":
			content = <Input type="date" size={size} variant={variant} disabled={disabled} name={name} label={label} data={data.container} onChange={() => onFieldChange?.(name)} />;
			break;

		case "birthday":
			content = <Input type="birthday" size={size} variant={variant} disabled={disabled} name={name} label={label} data={data.container} onChange={() => onFieldChange?.(name)} />;
			break;

		case "href":
			if (!data.container[name]) return null;
			content = (
				<Box
					element="button"
					type="button"
					disabled={disabled}
					aria-label={String(data.container[name])}
					onClick={() => {
						if (onComponentClick) onComponentClick(data);
					}}
					sx={{
						appearance: "none",
						display: "inline-flex",
						alignItems: "center",
						border: "0",
						backgroundColor: "transparent",
						padding: "0",
						minHeight: "32px",
						font: "inherit",
						cursor: "pointer",
						pointerEvents: disabled ? "none" : "all",
						borderRadius: "6px",
						"&:focus-visible": { outline: "3px solid rgba(37, 99, 235, 0.18)", outlineOffset: "2px" },
						"@media (pointer: coarse)": { minHeight: "44px" },
					}}>
					<Typo
						variant="medium"
						sx={{
							textDecoration: "underline",
							cursor: "pointer",
							textUnderlineOffset: "3px",
							textDecorationThickness: "1px",
							textDecorationSkipInk: "auto",
							pointerEvents: disabled ? "none" : "all",
						}}>
						{data.container[name]}
					</Typo>
				</Box>
			);
			break;

		case "flag":
			if (!data.container[name]) return null;
			content = (
				<Box
					sx={{
						width: "35px",
						height: "25px",
						minWidth: "35px",
						minHeight: "25px",
						backgroundSize: "cover",
						boxShadow: "0px 1px 2px rgba(0,0,0,.2)",
						border: "2px solid #FFFFFF",
						borderRadius: "2px",
					}}
					style={{
						backgroundImage: "url(https://flagcdn.com/" + data.container[name]?.toLowerCase() + ".svg",
					}}
				/>
			);
			break;

		case "boolean":
			content = <Checkbox disabled={disabled} name={name} sx={{}} label={label} data={data.container} onChange={() => onFieldChange?.(name)} />;
			break;

		case "textarea":
			content = (
				<Input
					size={size}
					variant={variant}
					type="textarea"
					disabled={disabled}
					name={name}
					label={label}
					data={data.container}
					sx={{ minHeight: "100px", ...(sx ?? {}) }}
					onChange={() => onFieldChange?.(name)}
				/>
			);
			break;

		case "sha256":
			content = (
				<HBox gap="10px">
					<Input
						disabled={disabled}
						key={state}
						name={name}
						size={size}
						variant={variant}
						label={label}
						data={data.container}
						sx={{ flexGrow: "1" }}
						onChange={() => onFieldChange?.(name)}
						iconRight={
							<Button
								variant="icon"
								sx={{ padding: "14px", margin: "2px", aspectRatio: "1 / 1" }}
								icon={<IconCopy size={"12"} />}
								onClick={async () => {
									if (data.container[name]) {
										if ("clipboard" in navigator) {
											await navigator.clipboard.writeText(data.container[name]);
										} else {
											document.execCommand("copy", true, data.container[name]);
										}
										toast.info("Copied", {
											position: "top-center",
											autoClose: 2000,
											hideProgressBar: true,
											closeOnClick: true,
											pauseOnHover: true,
											draggable: false,
											progress: undefined,
											theme: "light",
											transition: Slide,
										});
									}
								}}
							/>
						}
					/>
					<Button
						disabled={disabled}
						sx={{ ...getReferenceFormButtonSx(variant), alignSelf: "end", height: variant === "reference" ? "30px" : "46px" }}
						variant="secondarySmall"
						onClick={() => {
							const process = () => {
								data.container[name] = "";
								setState(state + 1);
								onFieldChange?.(name);
							};
							if (data.container[name]) {
								S_CONFIRM.invoke({
									message: "Confirm token clear?",
									onResult: (val: "confirm" | "reject") => {
										if (val === "confirm") {
											process();
										}
									},
								});
							} else {
								process();
							}
						}}
						title="Clear"
					/>
					<Button
						sx={{ ...getReferenceFormButtonSx(variant), alignSelf: "end", height: variant === "reference" ? "30px" : "46px" }}
						variant="secondarySmall"
						disabled={disabled}
						onClick={async () => {
							const process = async () => {
								const newValue = await randomSHA256();
								data.container[name] = newValue;
								setState(state + 1);
								onFieldChange?.(name);
							};
							if (data.container[name]) {
								S_CONFIRM.invoke({
									message: "Confirm token change?",
									onResult: (val: "confirm" | "reject") => {
										if (val === "confirm") {
											process();
										}
									},
								});
							} else {
								process();
							}
						}}
						title="Generate"
					/>
				</HBox>
			);
			break;

		default: {
			let customName = undefined;
			let customData = undefined;
			let useCustomObject = false;

			if (previewMode && data.options?.type === "select" && data.options?.values && Array.isArray(data.options.values) && data.options.values.length > 0) {
				if (typeof data.options.values[0] === "object") {
					const selectedValue = data.options.values.find((v: any) => v.value === data.container[name]);
					if (selectedValue && selectedValue.label && selectedValue.value) {
						useCustomObject = true;
						customName = "label";
						customData = selectedValue;
					}
				}
			}

			content = (
				<Input
					id={"form_input_" + name}
					disabled={disabled}
					size={size}
					variant={variant}
					name={useCustomObject && customName ? customName : name}
					label={label}
					units={units}
					formatAmount={type === "amount"}
					slider={data.options?.slider}
					asPercent={type === "%"}
					data={useCustomObject ? customData : data.container}
					type={type === "switchbox" ? "switchbox" : type === "password" ? "password" : type === "email" ? "email" : type === "number" ? "number" : "text"}
					autoComplete={data.options?.autoComplete}
					switchboxLabels={switchboxLabels}
					refvalue={data?.options?.refvalue}
					validator={data?.options?.validator as any}
					onChange={() => onFieldChange?.(name)}
				/>
			);
		}
	}

	const details = typeof data.options?.details === "string" ? data.options.details.trim() : "";
	if (details && data.options?.details_tooltip) {
		content = (
			<Box sx={{ position: "relative", "& label": { display: "block", paddingRight: "24px" } }}>
				{content}
				<FieldHint label={`About ${label ?? createName(name)}`} text={details} />
			</Box>
		);
	} else if (details) {
		content = (
			<VBox gap="6px">
				{content}
				<Typo variant="caption" sx={{ color: "#64748B", lineHeight: 1.5 }}>
					{details}
				</Typo>
			</VBox>
		);
	}

	if (!highlightedFields?.includes(name)) return <Box data-form-field={name} sx={{ minWidth: 0 }}>{content}</Box>;

	return (
		<Box
			data-form-field={name}
			id={`form_field_${name}`}
			sx={{
				padding: "8px",
				margin: "-8px",
				borderRadius: "12px",
				backgroundColor: "#FFFBEB",
				boxShadow: "0 0 0 2px #F59E0B",
				scrollMargin: "24px",
			}}>
			{content}
		</Box>
	);
};

const HINT_WIDTH = 240;
const HINT_MARGIN = 8;

// Portaled and clamped to the dialog (or viewport) so narrow edge fields never clip it.
// ponytail: position is captured on open; closes on scroll instead of tracking it.
const FieldHint = ({ label, text }: { label: string; text: string }) => {
	const [position, setPosition] = useState<{ left: number; top: number; width: number } | null>(null);
	const open = (event: { currentTarget: HTMLButtonElement }) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const bounds = event.currentTarget.closest("[data-dialog-surface]")?.getBoundingClientRect() ?? { left: 0, right: window.innerWidth };
		const width = Math.min(HINT_WIDTH, bounds.right - bounds.left - HINT_MARGIN * 2);
		const left = Math.min(Math.max(rect.right - width, bounds.left + HINT_MARGIN), bounds.right - width - HINT_MARGIN);
		setPosition({ left, top: rect.bottom + 6, width });
	};
	const close = () => setPosition(null);
	useEffect(() => {
		if (!position) return;
		window.addEventListener("scroll", close, true);
		return () => window.removeEventListener("scroll", close, true);
	}, [position]);

	return (
		<>
			<button
				type="button"
				aria-label={label}
				aria-expanded={Boolean(position)}
				onMouseEnter={open}
				onMouseLeave={close}
				onFocus={open}
				onBlur={close}
				onKeyDown={event => event.key === "Escape" && close()}
				style={{ position: "absolute", top: "1px", right: 0, width: "16px", height: "16px", padding: 0, borderRadius: "50%", border: 0, background: "#E2E8F0", color: "#475569", fontSize: "10px", fontWeight: 600, lineHeight: "16px", cursor: "help" }}>
				?
			</button>
			{position && createPortal(
				<div role="tooltip" style={{ position: "fixed", left: position.left, top: position.top, zIndex: 2000, width: position.width, boxSizing: "border-box", padding: "8px 10px", borderRadius: "8px", backgroundColor: "#111827", color: "#FFFFFF", fontSize: "12px", lineHeight: 1.45, boxShadow: "0 10px 24px rgba(15, 23, 42, 0.18)", pointerEvents: "none" }}>
					{text}
				</div>,
				document.body,
			)}
		</>
	);
};

export const createName = (str: string): string => {
	if (!str) return "";
	str = str.replaceAll("pluss_pluss", "++");
	str = str.replaceAll("e_mail", "e-mail");
	str = str.replaceAll("_", " ").trim();

	const parts = str.split(" ");

	if (str.toLowerCase() === "three ds") return "3DS";

	for (let i = 0; i < parts.length; i++) {
		let lstr = parts[i].toLowerCase();
		if (lstr === "firstname") lstr = "First Name";
		if (lstr === "lastname") lstr = "Last Name";
		if (lstr === "middlename") lstr = "Middle Name";
		if (lstr === "phonenumber") lstr = "Phone Number";
		if (lstr === "zipcode") lstr = "Zip Code";
		if (lstr === "postalcode") lstr = "Postal Code";
		if (lstr === "cvv") lstr = "CVV";
		if (lstr === "ssn") lstr = "SSN";
		if (lstr === "dob") lstr = "Date of Birth";
		if (lstr === "email") lstr = "e-mail";
		if (lstr === "id") lstr = "ID";
		if (lstr === "mcc") lstr = "MCC";
		if (lstr === "url") lstr = "URL";
		if (lstr === "ip") lstr = "IP";
		if (lstr === "3ds") lstr = "3DS";
		if (lstr === "pci") lstr = "PCI";
		if (lstr === "dss") lstr = "DSS";
		parts[i] = lstr;
	}

	str = parts.join(" ");
	return str.charAt(0).toUpperCase() + str.slice(1);
};

const isObjectEmpty = (obj: any): boolean => {
	let fields = 0;
	let emptyFields = 0;
	if (typeof obj !== "object" || obj === null || obj === "") return true;
	const keys = Object.keys(obj);

	for (const i in keys) {
		if (keys[i].startsWith("$__")) {
			continue;
		}

		fields++;
		if (typeof obj[keys[i]] === "object") {
			if (isObjectEmpty(obj[keys[i]])) {
				emptyFields++;
			}
			continue;
		}

		if (obj["$__" + keys[i] + "_hidden"] || obj["$__" + keys[i] + "_type"] === "delete_button") {
			emptyFields++;
			continue;
		}

		if (obj[keys[i]] === "" || obj[keys[i]] === null || obj[keys[i]] === undefined) emptyFields++;
		if (emptyFields > 0 && (obj[keys[i]] === 0 || obj[keys[i]] === false)) {
			emptyFields++;
		}
	}

	return emptyFields === fields;
};
